import React, { useEffect, useRef } from 'react';
import { CheckCircle, AlertTriangle, XCircle, X } from 'lucide-react';

export type ToastType = 'success' | 'warning' | 'error';

export interface ToastProps {
  id: string;
  message: React.ReactNode;
  type: ToastType;
  duration?: number;
  onDismiss: (id: string) => void;
  actionText?: string;
  onActionClick?: () => void;
}

const toastStyles = {
  success: {
    container: 'bg-[#ECFDF3] text-[#027A48] border-[#6CE9A6]',
    icon: <CheckCircle className="w-5 h-5" />,
    button: 'hover:bg-[#D1FADF] active:bg-[#D1FADF]',
  },
  warning: {
    container: 'bg-[#FFFAEB] text-[#B54708] border-[#FEC84B]',
    icon: <AlertTriangle className="w-5 h-5" />,
    button: 'hover:bg-[#FEF0C7] active:bg-[#FEF0C7]',
  },
  error: {
    container: 'bg-[#FEF3F2] text-[#B42318] border-[#FCA5A5]',
    icon: <XCircle className="w-5 h-5" />,
    button: 'hover:bg-[#FEE4E2] active:bg-[#FEE4E2]',
  },
};

export const Toast: React.FC<ToastProps> = ({
  id,
  message,
  type,
  duration = 5000,
  onDismiss,
  actionText,
  onActionClick,
}) => {
  const toastRef = useRef<HTMLDivElement>(null);
  let touchStartX = 0;
  let touchStartY = 0;

  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        onDismiss(id);
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [id, duration, onDismiss]);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX = e.touches[0].clientX;
    touchStartY = e.touches[0].clientY;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!toastRef.current) return;

    const deltaX = touchStartX - e.touches[0].clientX;
    const deltaY = touchStartY - e.touches[0].clientY;

    // Only handle horizontal swipes (ignore vertical scrolling)
    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      e.preventDefault();
      
      // Limit the swipe distance
      const swipeDistance = Math.min(Math.abs(deltaX), 100);
      const opacity = 1 - (swipeDistance / 100);
      
      toastRef.current.style.transform = `translateX(${deltaX > 0 ? -swipeDistance : swipeDistance}px)`;
      toastRef.current.style.opacity = opacity.toString();
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!toastRef.current) return;

    const deltaX = touchStartX - e.changedTouches[0].clientX;
    const deltaY = touchStartY - e.changedTouches[0].clientY;

    // Reset styles if swipe wasn't far enough
    if (Math.abs(deltaX) < 80 || Math.abs(deltaY) > Math.abs(deltaX)) {
      toastRef.current.style.transform = '';
      toastRef.current.style.opacity = '1';
    } else {
      // Dismiss the toast if swiped far enough
      onDismiss(id);
    }
  };

  return (
    <div
      ref={toastRef}
      className={`
        flex items-center gap-3 p-4 rounded-lg border shadow-sm
        animate-slide-up select-none touch-pan-y
        min-w-[280px] max-w-[calc(100vw-2rem)] sm:min-w-[320px] sm:max-w-[480px]
        transition-transform duration-200 ease-out
        ${type && toastStyles[type]?.container || toastStyles.success.container}
      `}
      role="alert"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div className="shrink-0">
        {type && toastStyles[type]?.icon || toastStyles.success.icon}
      </div>

      <div className="flex-grow text-sm font-medium break-words">
        {message}
      </div>

      <div className="flex items-center gap-2 shrink-0 ml-2">
        {actionText && onActionClick && (
          <button
            onClick={onActionClick}
            className={`
              text-sm font-semibold px-3 py-2 rounded
              ${type && toastStyles[type]?.button || toastStyles.success.button}
              transition-colors duration-200
              touch-manipulation
            `}
          >
            {actionText}
          </button>
        )}

        <button
          onClick={() => onDismiss(id)}
          className={`
            p-2 rounded-full
            ${type && toastStyles[type]?.button || toastStyles.success.button}
            transition-colors duration-200
            touch-manipulation
          `}
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};